import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuid } from 'uuid';
import './ModalProvider.scss';

type StackItem = { id: string; content: ReactNode };

type ModalContextType = {
	openModal: (content: ReactNode) => () => void;
	closeModal: (id?: string) => void; // default: close top
	closeAll: () => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }): JSX.Element => {
	const [stack, setStack] = useState<StackItem[]>([]);

	const closeModal = useCallback((id?: string): void => {
		setStack((s) => (id == null ? s.slice(0, -1) : s.filter((m) => m.id !== id)));
	}, []);

	const openModal = useCallback(
		(content: ReactNode): (() => void) => {
			const id = uuid();

			setStack((s) => [...s, { id, content }]);

			return () => closeModal(id);
		},
		[closeModal]
	);

	const closeAll = useCallback((): void => setStack([]), []);

	// lock body scroll while any modal is open
	useEffect(() => {
		if (stack.length > 0) {
			const prev = document.body.style.overflow;

			document.body.style.overflow = 'hidden';

			return () => {
				document.body.style.overflow = prev;
			};
		}
	}, [stack.length]);

	// Esc to close top (if dismissible)
	useEffect(() => {
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === 'Escape' && stack.length) {
				const top = stack[stack.length - 1];

				closeModal(top.id);
			}
		};

		window.addEventListener('keydown', onKey);

		return () => window.removeEventListener('keydown', onKey);
	}, [closeModal, stack]);

	const value = useMemo(() => ({ openModal, closeModal, closeAll }), [openModal, closeModal, closeAll]);

	return (
		<ModalContext.Provider value={value}>
			{children}

			{stack.map((item, i) =>
				createPortal(
					<div
						key={item.id}
						className="modalOverlay"
						style={{ zIndex: 10000 + i * 2 }}
						onClick={(): void => {
							if (i === stack.length - 1) {
								closeModal(item.id);
							}
						}}
					>
						<div className="modalContent" style={{ zIndex: 10001 + i * 2 }} onClick={(e): void => e.stopPropagation()}>
							{item.content}
						</div>
					</div>,
					document.body
				)
			)}
		</ModalContext.Provider>
	);
};

export function useModal(): ModalContextType {
	const ctx = useContext(ModalContext);

	if (!ctx) {
		throw new Error('useModal must be used inside ModalProvider');
	}

	return ctx;
}
